import type { CustomDateRange, DateRangeFilter } from '@/domain/filters';

/**
 * Query key factory for type-safe, consistent cache keys.
 * Organized by domain for easy invalidation patterns.
 */
export const queryKeys = {
    // Stats queries (dashboard tiles)
    stats: {
        all: ['stats'] as const,
        excludedVisitors: (
            domain: string,
            clientFilterKey: string = '',
            staffFilterKey: string = ''
        ) => ['stats', 'excludedVisitors', domain, clientFilterKey, staffFilterKey] as const,
        conversations: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'conversations',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        widgetDisplay: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'widgetDisplay',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        demoBooked: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) => ['stats', 'demoBooked', domain, timeRange, customRange ?? null, excludedKey] as const,
        ctaClicked: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) => ['stats', 'ctaClicked', domain, timeRange, customRange ?? null, excludedKey] as const,
        accounts: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) => ['stats', 'accounts', domain, timeRange, customRange ?? null, excludedKey] as const,
        visitors: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) => ['stats', 'visitors', domain, timeRange, customRange ?? null, excludedKey] as const,
        engagementRate: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'engagementRate',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        conversionRate: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'conversionRate',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        emailCaptureRate: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'emailCaptureRate',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        emailCaptured: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'stats',
                'emailCaptured',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
    },

    // Analytics queries (Analytics page)
    analytics: {
        all: ['analytics'] as const,
        roseImpact: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange: CustomDateRange | null | undefined,
            environment: string,
            excludedKey: string
        ) =>
            [
                'analytics',
                'roseImpact',
                domain,
                timeRange,
                customRange ?? null,
                environment,
                excludedKey,
            ] as const,
        conversationsOverTime: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'conversationsOverTime',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        ctaClicksOverTime: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'ctaClicksOverTime',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        turnsPercentilesOverTime: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'turnsPercentilesOverTime',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        funnel: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'funnel',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        conversionLabels: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange: CustomDateRange | null | undefined,
            excludedKey: string,
            rulesKey: string
        ) =>
            [
                'analytics',
                'conversionLabels',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
                rulesKey,
            ] as const,
        conversionLabelsOverTime: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange: CustomDateRange | null | undefined,
            excludedKey: string,
            rulesKey: string
        ) =>
            [
                'analytics',
                'conversionLabelsOverTime',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
                rulesKey,
            ] as const,
        observedConversionSignals: (domain: string) =>
            ['analytics', 'observedConversionSignals', domain] as const,
        postQualificationFunnel: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'postQualificationFunnel',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        qualificationCriteriaBreakdown: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange: CustomDateRange | null | undefined,
            environment: string
        ) =>
            [
                'analytics',
                'qualificationCriteriaBreakdown',
                domain,
                timeRange,
                customRange ?? null,
                environment,
            ] as const,
        conversationJourney: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'conversationJourney',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        questions: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange: CustomDateRange | null | undefined,
            language: string | null | undefined,
            environment: string | undefined,
            excludedKey: string,
            sourcesKey: string,
            groupByPage: boolean
        ) =>
            [
                'analytics',
                'questions',
                domain,
                timeRange,
                customRange ?? null,
                language ?? null,
                environment ?? 'production',
                excludedKey,
                sourcesKey,
                groupByPage,
            ] as const,
        pages: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'pages',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        urlParameters: (domain: string) => ['analytics', 'urlParameters', domain] as const,
        conversationSources: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'conversationSources',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        devices: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'devices',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
        cookielessSessions: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string
        ) =>
            [
                'analytics',
                'cookielessSessions',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
            ] as const,
        workspaces: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'workspaces',
                domain,
                timeRange,
                customRange ?? null,
                excludedKey,
            ] as const,
        trafficSourceFunnel: (
            domain: string,
            timeRange: DateRangeFilter,
            customRange?: CustomDateRange | null,
            environment?: string,
            excludedKey: string = ''
        ) =>
            [
                'analytics',
                'trafficSourceFunnel',
                domain,
                timeRange,
                customRange ?? null,
                environment ?? 'production',
                excludedKey,
            ] as const,
    },

    insights: {
        all: ['insights'] as const,
        list: (domain: string) => ['insights', 'list', domain] as const,
    },

    // Conversations queries
    conversations: {
        all: ['conversations'] as const,
        list: (domain: string, filters: Record<string, unknown>) =>
            ['conversations', 'list', domain, filters] as const,
        highlights: (domain: string, limit: number, isStaff?: boolean) =>
            ['conversations', 'highlights', domain, limit, isStaff] as const,
        messages: (conversationId: string) =>
            ['conversations', 'messages', conversationId] as const,
        byIds: (domain: string, ids: string[]) => ['conversations', 'byIds', domain, ids] as const,
        bySessionId: (sessionId: string) => ['conversations', 'bySessionId', sessionId] as const,
        search: (domain: string, query: string, params: Record<string, unknown>) =>
            ['conversations', 'search', domain, query, params] as const,
        distinctPageUrls: (domain: string) =>
            ['conversations', 'distinctPageUrls', domain] as const,
    },

    // Visitors queries
    visitors: {
        all: ['visitors'] as const,
        list: (domain: string, filters: Record<string, unknown>) =>
            ['visitors', 'list', domain, filters] as const,
        conversations: (visitorId: string) => ['visitors', 'conversations', visitorId] as const,
        sessions: (visitorId: string) => ['visitors', 'sessions', visitorId] as const,
        qualification: (visitorId: string) => ['visitors', 'qualification', visitorId] as const,
        identitySource: (visitorId: string) => ['visitors', 'identitySource', visitorId] as const,
    },

    // Accounts queries
    accounts: {
        all: ['accounts'] as const,
        list: (domain: string, filters: Record<string, unknown>) =>
            ['accounts', 'list', domain, filters] as const,
        hot: (
            domain: string,
            dateRange: DateRangeFilter,
            customRange: CustomDateRange | null,
            limit: number,
            isStaff: boolean
        ) => ['accounts', 'hot', domain, dateRange, customRange, limit, isStaff] as const,
        byId: (accountId: string) => ['accounts', 'byId', accountId] as const,
        topicsKeywords: (accountId: string, domain: string) =>
            ['accounts', 'topicsKeywords', accountId, domain] as const,
        enrichmentInfo: (accountId: string) => ['accounts', 'enrichmentInfo', accountId] as const,
    },

    // Topics queries
    topics: {
        all: ['topics'] as const,
        list: (
            domain: string,
            dateRange: string,
            environment?: string,
            customRange?: CustomDateRange | null,
            initialQuestionType?: string,
            minTurns?: number
        ) =>
            [
                'topics',
                'list',
                domain,
                dateRange,
                environment,
                customRange ?? null,
                initialQuestionType ?? 'all',
                minTurns ?? 1,
            ] as const,
        distinct: (domain: string) => ['topics', 'distinct', domain] as const,
    },

    // Session metrics queries
    sessionMetrics: {
        all: ['sessionMetrics'] as const,
        visitor: (visitorId: string) => ['sessionMetrics', 'visitor', visitorId] as const,
        account: (accountId: string, domain: string) =>
            ['sessionMetrics', 'account', accountId, domain] as const,
        multipleVisitors: (visitorIds: string[], domain: string) =>
            ['sessionMetrics', 'multipleVisitors', visitorIds, domain] as const,
    },

    // Page views queries
    pageViews: {
        all: ['pageViews'] as const,
        session: (sessionId: string) => ['pageViews', 'session', sessionId] as const,
        multipleSessions: (sessionIds: string[]) =>
            ['pageViews', 'multipleSessions', sessionIds] as const,
    },

    // Session events queries (CTA clicked, form submitted)
    sessionEvents: {
        all: ['sessionEvents'] as const,
        multipleSessions: (sessionIds: string[]) =>
            ['sessionEvents', 'multipleSessions', sessionIds] as const,
    },

    // AI Sections queries
    aiSections: {
        all: ['aiSections'] as const,
        list: (domain: string) => ['aiSections', 'list', domain] as const,
    },

    // Curated content queries
    curatedContent: {
        all: ['curatedContent'] as const,
        list: (domain: string) => ['curatedContent', 'list', domain] as const,
    },

    // Knowledge FAQ queries
    knowledgeFaqs: {
        all: ['knowledgeFaqs'] as const,
        list: (workspaceId: string) => ['knowledgeFaqs', 'list', workspaceId] as const,
    },

    // Knowledge documents queries (custom knowledge docs)
    knowledgeDocuments: {
        all: ['knowledgeDocuments'] as const,
        list: (workspaceId: string) => ['knowledgeDocuments', 'list', workspaceId] as const,
        body: (id: string, workspaceId: string) =>
            ['knowledgeDocuments', 'body', workspaceId, id] as const,
    },

    // Playground: retrieval source paths resolved back to knowledge rows
    playgroundKnowledge: {
        all: ['playgroundKnowledge'] as const,
        sources: (domain: string, pathsKey: string) =>
            ['playgroundKnowledge', 'sources', domain, pathsKey] as const,
        // Own namespace on purpose: the panel caches the markdown string, while the
        // Knowledge pages cache the whole body response under their own keys. Sharing a
        // key would hand one page the other's shape.
        body: (id: string, workspaceId: string) =>
            ['playgroundKnowledge', 'body', workspaceId, id] as const,
    },

    // Playground answer datasets: the curated questions and every run's results.
    playgroundDatasets: {
        all: ['playgroundDatasets'] as const,
        questions: (domain: string) => ['playgroundDatasets', 'questions', domain] as const,
        // One key for the whole result history — a dataset is capped at 50
        // questions, so the page derives "latest per question" in memory and the
        // same cache entry backs the per-row history drawer.
        runItems: (domain: string) => ['playgroundDatasets', 'runItems', domain] as const,
        // Fetched per opened row, not with the list — see fetchRunItemKnowledge.
        knowledge: (itemId: string) => ['playgroundDatasets', 'knowledge', itemId] as const,
        // Whether AI drafting has anything to ground on for this workspace.
        grounding: (domain: string) => ['playgroundDatasets', 'grounding', domain] as const,
    },

    // Dormant pricing guidance, stored independently from retrievable documents.
    knowledgePricing: {
        all: ['knowledgePricing'] as const,
        detail: (workspaceId: string) => ['knowledgePricing', 'detail', workspaceId] as const,
    },

    // Knowledge Lookup tables queries
    knowledgeLookup: {
        all: ['knowledgeLookup'] as const,
        list: (domain: string) => ['knowledgeLookup', 'list', domain] as const,
        entries: (
            domain: string,
            tableId: string,
            page: number,
            search: string,
            sort: { key: string; dir: string }
        ) =>
            [
                'knowledgeLookup',
                'entries',
                domain,
                tableId,
                page,
                search,
                sort.key,
                sort.dir,
            ] as const,
        entriesAll: (domain: string, tableId: string) =>
            ['knowledgeLookup', 'entries', domain, tableId] as const,
        counts: (domain: string, tableIds: string[]) =>
            ['knowledgeLookup', 'counts', domain, [...tableIds].sort().join(',')] as const,
    },

    // Website sync mapping queries
    websiteSync: {
        all: ['websiteSync'] as const,
        capabilities: () => ['websiteSync', 'capabilities'] as const,
        unfinishedDocuments: (workspaceId: string) =>
            ['websiteSync', 'unfinishedDocuments', workspaceId] as const,
        runs: (workspaceId: string) => ['websiteSync', 'runs', workspaceId] as const,
        sources: (workspaceId: string) => ['websiteSync', 'sources', workspaceId] as const,
        run: (workspaceId: string, runId: string) =>
            ['websiteSync', 'run', workspaceId, runId] as const,
        pagesAll: (workspaceId: string) => ['websiteSync', 'pages', workspaceId] as const,
        pages: (
            workspaceId: string,
            filters: Readonly<{
                decision?: unknown;
                search?: unknown;
                page?: unknown;
                pageSize?: unknown;
            }>
        ) => ['websiteSync', 'pages', workspaceId, filters] as const,
        body: (documentId: string, workspaceId: string) =>
            ['websiteSync', 'body', workspaceId, documentId] as const,
        graphCounts: (workspaceId: string) => ['websiteSync', 'graphCounts', workspaceId] as const,
        lastIngestion: (workspaceId: string) =>
            ['websiteSync', 'lastIngestion', workspaceId] as const,
        everIngested: (workspaceId: string) =>
            ['websiteSync', 'everIngested', workspaceId] as const,
        newlySelected: (workspaceId: string, sinceIso: string | null) =>
            ['websiteSync', 'newlySelected', workspaceId, sinceIso] as const,
        rescanReviewsAll: (workspaceId: string) =>
            ['websiteSync', 'rescanReviews', workspaceId] as const,
        applyFailures: (workspaceId: string, operationId: string) =>
            ['websiteSync', 'rescanReviews', workspaceId, 'applyFailures', operationId] as const,
        currentRescanReview: (workspaceId: string) =>
            ['websiteSync', 'rescanReviews', workspaceId, 'current'] as const,
        rescanReviewPages: (workspaceId: string, reviewId: string) =>
            ['websiteSync', 'rescanReviews', workspaceId, reviewId, 'pages'] as const,
    },

    // GEO Questions queries
    geoQuestions: {
        all: ['geoQuestions'] as const,
        list: (workspaceId: string) => ['geoQuestions', 'list', workspaceId] as const,
    },

    // GEO FAQs queries
    geoFaqs: {
        all: ['geoFaqs'] as const,
        list: (workspaceId: string) => ['geoFaqs', 'list', workspaceId] as const,
        proposals: (workspaceId: string, language: string) =>
            ['geoFaqs', 'proposals', workspaceId, language] as const,
        translations: (workspaceId: string) => ['geoFaqs', 'translations', workspaceId] as const,
        changes: (workspaceId: string) => ['geoFaqs', 'changes', workspaceId] as const,
    },

    geoRuns: {
        all: ['geoRuns'] as const,
        latest: (workspaceId: string) => ['geoRuns', 'latest', workspaceId] as const,
    },

    r4aSurfaceChecks: {
        latest: (workspaceId: string) => ['r4aSurfaceChecks', 'latest', workspaceId] as const,
    },

    // GEO Publishing (ixadmin_api republish trigger + status)
    geoSections: {
        all: ['geoSections'] as const,
        list: (workspaceId: string, language: string) =>
            ['geoSections', 'list', workspaceId, language] as const,
    },
    geoPublish: {
        all: ['geoPublish'] as const,
        lastRun: (domain: string) => ['geoPublish', 'lastRun', domain] as const,
        customHostname: (domain: string, host: string) =>
            ['geoPublish', 'customHostname', domain, host] as const,
        proxyHealth: (workspaceId: string) => ['geoPublish', 'proxyHealth', workspaceId] as const,
    },

    // Integrations queries
    integrations: {
        all: ['integrations'] as const,
        hubspotStatus: (domain: string) => ['integrations', 'hubspot', 'status', domain] as const,
        hubspotClientSettings: (clientId: string, domain: string) =>
            ['integrations', 'hubspot', 'client-settings', clientId, domain] as const,
    },

    // Agents queries
    agents: {
        all: ['agents'] as const,
        workspaceConfig: (domain: string) => ['agents', 'workspace-config', domain] as const,
    },

    // Clients queries (admin)
    clients: {
        all: ['clients'] as const,
        list: () => ['clients', 'list'] as const,
        stats30d: () => ['clients', 'stats30d'] as const,
        sessionStats30d: () => ['clients', 'sessionStats30d'] as const,
        roots: (clientId: string) => ['clients', 'roots', clientId] as const,
    },

    // Users queries (admin)
    users: {
        all: ['users'] as const,
        list: () => ['users', 'list'] as const,
        domains: () => ['users', 'domains'] as const,
        domainClients: () => ['users', 'domainClients'] as const,
    },

    // Team queries (client account admin — IX-3872)
    team: {
        all: ['team'] as const,
        members: (domain: string) => ['team', 'members', domain] as const,
        account: (domain: string) => ['team', 'account', domain] as const,
    },

    // Backoffice auth queries
    backofficeAuth: {
        all: ['backofficeAuth'] as const,
        user: (userId: string | null) => ['backofficeAuth', 'user', userId] as const,
    },

    // Support tickets queries
    supportTickets: {
        all: ['supportTickets'] as const,
        list: (domain: string) => ['supportTickets', 'list', domain] as const,
        reportedSessions: (domain: string) =>
            ['supportTickets', 'reportedSessions', domain] as const,
    },

    // Unified config queries
    config: {
        all: ['config'] as const,
        resolved: (domain: string) => ['config', 'resolved', domain] as const,
        /** A workspace's own rows, under `resolved` so a save there refreshes them too. */
        ownRows: (configKey: string) => ['config', 'resolved', configKey, 'own-rows'] as const,
        selfServePlan: (domain: string) => ['config', 'selfServePlan', domain] as const,
        admin: ['config', 'admin'] as const,
    },

    modelInventory: {
        all: ['modelInventory'] as const,
        detail: (domain: string) => ['modelInventory', 'detail', domain] as const,
    },

    // Kept here so plan assignment can refresh IX-4742's in-progress usage surface
    // without coupling the Super Admin control to that page's implementation.
    planUsage: {
        all: ['planUsage'] as const,
        detail: (domain: string) => ['planUsage', 'detail', domain] as const,
    },

    // Publishable widget API key shown in the install snippet (IX-5066)
    publishableApiKey: {
        detail: (domain: string) => ['publishableApiKey', 'detail', domain] as const,
        widget: (domain: string) => ['publishableApiKey', 'widget', domain] as const,
    },

    // Stripe billing state of the client that owns the domain (IX-5059)
    billing: {
        status: (domain: string) => ['billing', 'status', domain] as const,
    },

    // Integration secrets (presence flags only — values never reach the browser)
    integrationSecrets: {
        all: ['integrationSecrets'] as const,
        status: (domain: string) => ['integrationSecrets', 'status', domain] as const,
    },

    // Staff A/B experiments (Mode A)
    staffExperiments: {
        all: ['staffExperiments'] as const,
        list: (includeArchived = false) =>
            ['staffExperiments', 'list', { includeArchived }] as const,
        row: (experimentId: string) => ['staffExperiments', 'row', experimentId] as const,
        pooled: (id: string) => ['staffExperiments', 'pooled', id] as const,
        perDomain: (id: string) => ['staffExperiments', 'perDomain', id] as const,
        sanity: (id: string) => ['staffExperiments', 'sanity', id] as const,
        eligibleClients: () => ['staffExperiments', 'eligibleClients'] as const,
        membership: (id: string) => ['staffExperiments', 'membership', id] as const,
        exclusionsHistory: (id: string) => ['staffExperiments', 'exclusionsHistory', id] as const,
    },

    // Staff identity diagnostics
    personIdentity: {
        all: ['personIdentity'] as const,
        comparison: (params: {
            domain: string;
            accountCoverage: string;
            personCoverage: string;
            agreement: string;
            provider: string;
            accountSource: string;
        }) => ['personIdentity', 'comparison', params] as const,
        stats: (domain: string) => ['personIdentity', 'stats', domain] as const,
        recent: (limit: number) => ['personIdentity', 'recent', limit] as const,
    },

    // Client per-domain A/B experiments (Mode B)
    clientExperiments: {
        all: ['clientExperiments'] as const,
        behaviorResults: (domain: string) =>
            ['clientExperiments', 'behaviorResults', domain] as const,
        active: (domain: string) => ['clientExperiments', 'active', domain] as const,
        activeStaff: (domain: string) => ['clientExperiments', 'activeStaff', domain] as const,
        history: (domain: string) => ['clientExperiments', 'history', domain] as const,
        row: (experimentId: string) => ['clientExperiments', 'row', experimentId] as const,
        prefill: (domain: string, excludedKey: string) =>
            ['clientExperiments', 'prefill', domain, excludedKey] as const,
    },

    // "What's new" product updates (IX-3975)
    productUpdates: {
        all: ['productUpdates'] as const,
        list: ['productUpdates', 'list'] as const,
        lastSeen: (userId: string | null) => ['productUpdates', 'lastSeen', userId] as const,
    },

    // Staff-authored global notice banner (IX-4223)
    notices: {
        all: ['notices'] as const,
        active: ['notices', 'active'] as const,
        list: ['notices', 'list'] as const,
    },

    // Per-conversation R4A buying activity (IX-5608)
    r4aTimeline: (sessionIds: string[]) => ['r4aTimeline', sessionIds] as const,
    // Super admin agent usage reports
    r4aUsage: (days: number, environment: string) => ['r4aUsage', days, environment] as const,
    webmcpUsage: (days: number, environment: string) => ['webmcpUsage', days, environment] as const,
    webmcpToolUsage: (days: number, environment: string) =>
        ['webmcpToolUsage', days, environment] as const,
    webmcpBrowserUsage: (days: number, environment: string) =>
        ['webmcpBrowserUsage', days, environment] as const,
    // Super admin Agent HTTP endpoint usage report (IX-5425)
    agentHttpUsage: (days: number, environment: string) =>
        ['agentHttpUsage', days, environment] as const,
    agentHttpClientUsage: (days: number, environment: string) =>
        ['agentHttpClientUsage', days, environment] as const,
    agentAuthFunnel: (days: number, environment: string) =>
        ['agentAuthFunnel', days, environment] as const,
    agentAuthRefusals: (days: number, environment: string) =>
        ['agentAuthRefusals', days, environment] as const,
    agentAuthSignups: (days: number, environment: string) =>
        ['agentAuthSignups', days, environment] as const,

    // Plans & prices shared across agents, and the Rose for Agents listing (IX-5245)
    pricingPlans: {
        workspace: (workspaceId: string) => ['pricingPlans', workspaceId] as const,
    },
    r4aListing: {
        workspace: (workspaceId: string) => ['r4aListing', workspaceId] as const,
    },
    r4aTrialProtection: {
        workspace: (workspaceId: string) => ['r4aTrialProtection', workspaceId] as const,
    },
    r4aAgentSurfaces: {
        workspace: (workspaceId: string) => ['r4aAgentSurfaces', workspaceId] as const,
    },
    r4aAgentTraffic: {
        workspace: (workspaceId: string, start: string, end: string, environment: string) =>
            ['r4aAgentTraffic', workspaceId, start, end, environment] as const,
    },
    r4aTrialMode: {
        domain: (siteDomain: string) => ['r4aTrialMode', siteDomain] as const,
    },

    health: {
        all: ['health'] as const,
        eventCounts: (domain: string) => ['health', 'eventCounts', domain] as const,
    },
} as const;
